import React, { useState, Component } from 'react';
import { ArrowLeftIcon, CalendarIcon, StickyNoteIcon, PhoneIcon, MailIcon, MapPinIcon, EditIcon, HashIcon, CopyIcon, CheckIcon, ListTodoIcon, UserPlusIcon, PlusIcon } from 'lucide-react';
import { Customer } from '../components/CustomerCard';
import { Task, TaskCard } from '../components/TaskCard';
import { Button } from '../components/Button';
import { motion } from 'framer-motion';
interface CustomerDetailProps {
  customer: Customer;
  tasks: Task[];
  onBack: () => void;
  onEdit: (customer: Customer) => void;
  onEditTask: (task: Task) => void;
  onDeleteTask: (task: Task) => void;
  onAddTask: (customerId: string) => void;
}
export function CustomerDetail({
  customer,
  tasks,
  onBack,
  onEdit,
  onEditTask,
  onDeleteTask,
  onAddTask
}: CustomerDetailProps) {
  const [copiedPhone, setCopiedPhone] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [copiedSecondaryPhone, setCopiedSecondaryPhone] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<'all' | Task['status']>('all');
  const copyToClipboard = async (text: string, type: 'phone' | 'address' | 'secondaryPhone') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'phone') {
        setCopiedPhone(true);
        setTimeout(() => setCopiedPhone(false), 2000);
      } else if (type === 'address') {
        setCopiedAddress(true);
        setTimeout(() => setCopiedAddress(false), 2000);
      } else if (type === 'secondaryPhone') {
        setCopiedSecondaryPhone(true);
        setTimeout(() => setCopiedSecondaryPhone(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  const customerTasks = tasks.filter(t => t.customerId === customer.id);
  const activeTasks = customerTasks.filter(t => t.status === 'in-progress').length;
  const completedTasks = customerTasks.filter(t => t.status === 'done').length;
  const plannedTasks = customerTasks.filter(t => t.status === 'planned').length;
  const pendingTasks = customerTasks.filter(t => t.status === 'pending').length;
  const filteredTasks = selectedStatus === 'all' ? customerTasks : customerTasks.filter(t => t.status === selectedStatus);
  return <div data-id="element-1260">
      <div className="flex items-center gap-4 mb-8" data-id="element-1261">
        <button onClick={onBack} className="p-2 text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-lg transition-all" aria-label="Tilbage" data-id="element-1262">
          <ArrowLeftIcon className="w-5 h-5" data-id="element-1263" />
        </button>
        <div className="flex-1" data-id="element-1264">
          <h1 className="text-4xl font-bold text-stone-900" data-id="element-1265">{customer.name}</h1>
          <div className="flex items-center gap-2 mt-2" data-id="element-1266">
            <HashIcon className="w-4 h-4 text-teal-600" data-id="element-1267" />
            <p className="text-teal-600 font-medium" data-id="element-1268">{customer.customerId}</p>
          </div>
        </div>
        <div className="flex gap-2" data-id="element-1269">
          <Button onClick={() => onAddTask(customer.id)} variant="primary" className="flex items-center gap-2" data-id="element-1270">
            <PlusIcon className="w-4 h-4" data-id="element-1271" />
            Tilføj opgave
          </Button>
          <Button onClick={() => onEdit(customer)} variant="secondary" className="flex items-center gap-2" data-id="element-1272">
            <EditIcon className="w-4 h-4" data-id="element-1273" />
            Rediger
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8" data-id="element-1274">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3
      }} className="bg-white rounded-xl p-6 shadow-sm border border-stone-100" data-id="element-1275">
          <div className="flex items-center gap-3 mb-2" data-id="element-1276">
            <div className="p-2 bg-blue-100 rounded-lg" data-id="element-1277">
              <CalendarIcon className="w-5 h-5 text-blue-700" data-id="element-1278" />
            </div>
            <h3 className="text-stone-600 text-sm font-medium" data-id="element-1279">
              Opgaver i alt
            </h3>
          </div>
          <p className="text-3xl font-bold text-stone-900" data-id="element-1280">
            {customerTasks.length}
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.05
      }} className="bg-white rounded-xl p-6 shadow-sm border border-stone-100" data-id="element-1281">
          <div className="flex items-center gap-3 mb-2" data-id="element-1282">
            <div className="p-2 bg-amber-100 rounded-lg" data-id="element-1283">
              <CalendarIcon className="w-5 h-5 text-amber-700" data-id="element-1284" />
            </div>
            <h3 className="text-stone-600 text-sm font-medium" data-id="element-1285">
              Aktive opgaver
            </h3>
          </div>
          <p className="text-3xl font-bold text-stone-900" data-id="element-1286">{activeTasks}</p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.1
      }} className="bg-white rounded-xl p-6 shadow-sm border border-stone-100" data-id="element-1287">
          <div className="flex items-center gap-3 mb-2" data-id="element-1288">
            <div className="p-2 bg-emerald-100 rounded-lg" data-id="element-1289">
              <CalendarIcon className="w-5 h-5 text-emerald-700" data-id="element-1290" />
            </div>
            <h3 className="text-stone-600 text-sm font-medium" data-id="element-1291">
              Afsluttede opgaver
            </h3>
          </div>
          <p className="text-3xl font-bold text-stone-900" data-id="element-1292">{completedTasks}</p>
        </motion.div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      delay: 0.15
    }} className="bg-white rounded-xl p-8 shadow-sm border border-stone-100" data-id="element-1293">
        <h2 className="text-2xl font-semibold text-stone-900 mb-6" data-id="element-1294">
          Kundeinformation
        </h2>

        <div className="grid md:grid-cols-2 gap-6" data-id="element-1295">
          {customer.address && <div className="flex items-start gap-4" data-id="element-1296">
              <div className="p-3 bg-teal-100 rounded-lg" data-id="element-1297">
                <MapPinIcon className="w-5 h-5 text-teal-700" data-id="element-1298" />
              </div>
              <div className="flex-1" data-id="element-1299">
                <p className="text-sm font-medium text-stone-500 mb-1" data-id="element-1300">
                  Adresse
                </p>
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(customer.address)}`} target="_blank" rel="noopener noreferrer" className="text-stone-900 hover:text-teal-600 transition-colors underline" data-id="element-1301">
                  {customer.address}
                </a>
              </div>
              <button onClick={() => copyToClipboard(customer.address, 'address')} className="p-2 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Kopiér adresse" data-id="element-1302">
                {copiedAddress ? <CheckIcon className="w-5 h-5 text-teal-600" data-id="element-1303" /> : <CopyIcon className="w-5 h-5" data-id="element-1304" />}
              </button>
            </div>}

          {customer.phone && <div className="flex items-start gap-4" data-id="element-1305">
              <div className="p-3 bg-teal-100 rounded-lg" data-id="element-1306">
                <PhoneIcon className="w-5 h-5 text-teal-700" data-id="element-1307" />
              </div>
              <div className="flex-1" data-id="element-1308">
                <p className="text-sm font-medium text-stone-500 mb-1" data-id="element-1309">
                  Telefon
                </p>
                <p className="text-stone-900 font-medium" data-id="element-1310">{customer.phone}</p>
              </div>
              <button onClick={() => copyToClipboard(customer.phone, 'phone')} className="p-2 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Kopiér telefonnummer" data-id="element-1311">
                {copiedPhone ? <CheckIcon className="w-5 h-5 text-teal-600" data-id="element-1312" /> : <CopyIcon className="w-5 h-5" data-id="element-1313" />}
              </button>
            </div>}

          {customer.email && <div className="flex items-start gap-4" data-id="element-1314">
              <div className="p-3 bg-teal-100 rounded-lg" data-id="element-1315">
                <MailIcon className="w-5 h-5 text-teal-700" data-id="element-1316" />
              </div>
              <div className="flex-1" data-id="element-1317">
                <p className="text-sm font-medium text-stone-500 mb-1" data-id="element-1318">Email</p>
                <a href={`mailto:${customer.email}`} className="text-stone-900 hover:text-teal-600 transition-colors" data-id="element-1319">
                  {customer.email}
                </a>
              </div>
            </div>}

          {customer.secondaryContactName && <div className="flex items-start gap-4" data-id="element-1320">
              <div className="p-3 bg-teal-100 rounded-lg" data-id="element-1321">
                <UserPlusIcon className="w-5 h-5 text-teal-700" data-id="element-1322" />
              </div>
              <div className="flex-1" data-id="element-1323">
                <p className="text-sm font-medium text-stone-500 mb-1" data-id="element-1324">
                  Ekstra kontaktperson
                </p>
                <p className="text-stone-900 font-medium" data-id="element-1325">
                  {customer.secondaryContactName}
                </p>
                {customer.secondaryContactPhone && <div className="flex items-center gap-2 mt-1" data-id="element-1326">
                    <p className="text-stone-600 text-sm" data-id="element-1327">
                      {customer.secondaryContactPhone}
                    </p>
                    <button onClick={() => copyToClipboard(customer.secondaryContactPhone!, 'secondaryPhone')} className="p-1 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Kopiér telefonnummer" data-id="element-1328">
                      {copiedSecondaryPhone ? <CheckIcon className="w-3 h-3 text-teal-600" data-id="element-1329" /> : <CopyIcon className="w-3 h-3" data-id="element-1330" />}
                    </button>
                  </div>}
              </div>
            </div>}
        </div>

        {customer.notes && <div className="mt-6 pt-6 border-t border-stone-200" data-id="element-1331">
            <div className="flex items-start gap-4" data-id="element-1332">
              <div className="p-3 bg-amber-100 rounded-lg" data-id="element-1333">
                <StickyNoteIcon className="w-5 h-5 text-amber-700" data-id="element-1334" />
              </div>
              <div className="flex-1" data-id="element-1335">
                <p className="text-sm font-medium text-stone-500 mb-2" data-id="element-1336">Noter</p>
                <p className="text-stone-700 leading-relaxed" data-id="element-1337">
                  {customer.notes}
                </p>
              </div>
            </div>
          </div>}
      </motion.div>

      {/* Customer Tasks Section */}
      {customerTasks.length > 0 && <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      delay: 0.2
    }} className="mt-8" data-id="element-1338">
          <div className="flex items-center justify-between mb-6" data-id="element-1339">
            <div className="flex items-center gap-3" data-id="element-1340">
              <div className="p-2 bg-amber-100 rounded-lg" data-id="element-1341">
                <ListTodoIcon className="w-5 h-5 text-amber-700" data-id="element-1342" />
              </div>
              <h2 className="text-2xl font-semibold text-stone-900" data-id="element-1343">
                Opgaver for {customer.name}
              </h2>
            </div>
          </div>

          {/* Status Filter Buttons */}
          <div className="flex flex-wrap gap-2 mb-6" data-id="element-1344">
            <button onClick={() => setSelectedStatus('all')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${selectedStatus === 'all' ? 'bg-stone-900 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1345">
              Alle ({customerTasks.length})
            </button>
            <button onClick={() => setSelectedStatus('in-progress')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${selectedStatus === 'in-progress' ? 'bg-amber-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1346">
              I gang ({activeTasks})
            </button>
            <button onClick={() => setSelectedStatus('done')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${selectedStatus === 'done' ? 'bg-emerald-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1347">
              Afsluttet ({completedTasks})
            </button>
            <button onClick={() => setSelectedStatus('planned')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${selectedStatus === 'planned' ? 'bg-blue-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1348">
              Planlagt ({plannedTasks})
            </button>
            <button onClick={() => setSelectedStatus('pending')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${selectedStatus === 'pending' ? 'bg-purple-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1349">
              Afventende ({pendingTasks})
            </button>
          </div>

          {/* Tasks Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1350">
            {filteredTasks.map((task, index) => <TaskCard key={task.id} task={task} index={index} onClick={() => onEditTask(task)} onDelete={onDeleteTask} data-id="element-1351" />)}
          </div>

          {filteredTasks.length === 0 && <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200" data-id="element-1352">
              <ListTodoIcon className="w-12 h-12 text-stone-300 mx-auto mb-3" data-id="element-1353" />
              <p className="text-stone-600 font-medium" data-id="element-1354">
                Ingen opgaver i denne kategori
              </p>
              <p className="text-sm text-stone-500 mt-1" data-id="element-1355">
                Prøv at vælge en anden status
              </p>
            </div>}
        </motion.div>}
    </div>;
}